'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { ArrowLeft, Send, Sparkles, RefreshCw, ChevronDown } from 'lucide-react';
import { useChat } from '@/lib/hooks/useChat';
import ReactMarkdown from 'react-markdown';
import toast, { Toaster } from 'react-hot-toast';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const SYSTEM_PROMPT = `You are Yagnick AI — a deeply knowledgeable, culturally respectful, and spiritually attuned guide for Hindu rituals, Vedic traditions, and priest matching. You embody the wisdom of authentic Vedic practices.

Your expertise includes:
1. **Ritual Guidance**: Detailed knowledge of Hindu poojas, yagnas, homas, samskaras (life rituals), vratas (fasts), and their correct procedures, timings, and significance.
2. **Sampradaya Education**: Deep understanding of major Hindu traditions — Shaiva, Vaishnava, Shakta, Smarta, and regional traditions. You explain lineages, philosophies, and practices respectfully.
3. **Priest Matching**: You ask intelligent questions to understand the user's sampradaya, language, regional tradition, ritual type, and occasion to recommend the most suitable priest profile.
4. **Astrological Context**: You understand muhurtas (auspicious timings), nakshatras, tithis, and their relevance to rituals.
5. **Multilingual Support**: You can respond in Hindi, Sanskrit (with transliteration), Tamil, Telugu, Kannada, Malayalam, Bengali, Marathi, or English based on user preference.

Guidelines:
- Always be respectful, warm, and use appropriate Sanskrit terms with explanations
- Provide culturally accurate information aligned with authentic Vedic texts (Vedas, Puranas, Agamas, Dharmashastra)
- When recommending priests, ask about: sampradaya, language preference, ritual type, occasion, location, and budget
- Use 🙏 and spiritual emojis appropriately but not excessively
- Structure responses clearly with headers when explaining complex rituals
- Always acknowledge regional variations in practices
- Begin responses with a warm spiritual greeting when appropriate`;

const QUICK_PROMPTS = [
  { emoji: '🏠', text: 'Griha Pravesh pooja guidance', category: 'ritual' },
  { emoji: '👶', text: 'Namkaran ceremony details', category: 'ritual' },
  { emoji: '📿', text: 'Explain Shaiva sampradaya', category: 'education' },
  { emoji: '⭐', text: 'Help me find the right priest', category: 'priest' },
  { emoji: '🌙', text: 'Satyanarayan Katha procedure', category: 'ritual' },
  { emoji: '🔥', text: 'Navgraha homa benefits', category: 'ritual' },
  { emoji: '🌺', text: 'Vaishnava traditions explained', category: 'education' },
  { emoji: '📅', text: 'Best muhurta for my event', category: 'timing' },
];

const CATEGORY_COLORS: Record<string, string> = {
  ritual: 'bg-saffron/10 border-saffron/30 text-saffron hover:bg-saffron/20',
  education: 'bg-gold/10 border-gold/30 text-gold hover:bg-gold/20',
  priest: 'bg-maroon/10 border-maroon/30 text-maroon hover:bg-maroon/20',
  timing: 'bg-sandalwood/10 border-sandalwood/30 text-sandalwood hover:bg-sandalwood/20',
};

// Mandala SVG paths for rotating background elements
const MANDALA_SVG = `data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='200' cy='200' r='190' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3Ccircle cx='200' cy='200' r='160' fill='none' stroke='%23D4AF37' stroke-width='0.6'/%3E%3Ccircle cx='200' cy='200' r='130' fill='none' stroke='%23D4AF37' stroke-width='0.5'/%3E%3Ccircle cx='200' cy='200' r='100' fill='none' stroke='%23D4AF37' stroke-width='0.5'/%3E%3Ccircle cx='200' cy='200' r='70' fill='none' stroke='%23D4AF37' stroke-width='0.4'/%3E%3Ccircle cx='200' cy='200' r='40' fill='none' stroke='%23D4AF37' stroke-width='0.4'/%3E%3Cline x1='200' y1='10' x2='200' y2='390' stroke='%23D4AF37' stroke-width='0.3'/%3E%3Cline x1='10' y1='200' x2='390' y2='200' stroke='%23D4AF37' stroke-width='0.3'/%3E%3Cline x1='55' y1='55' x2='345' y2='345' stroke='%23D4AF37' stroke-width='0.3'/%3E%3Cline x1='345' y1='55' x2='55' y2='345' stroke='%23D4AF37' stroke-width='0.3'/%3E%3Cpolygon points='200,30 220,180 370,200 220,220 200,370 180,220 30,200 180,180' fill='none' stroke='%23D4AF37' stroke-width='0.5'/%3E%3Ccircle cx='200' cy='200' r='15' fill='none' stroke='%23FF9933' stroke-width='1'/%3E%3C/svg%3E`;

export default function AIGuidePage() {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: '🙏 **Namaste!** I am Yagnick AI, your personal Vedic guide.\n\nI can help you with:\n- **Ritual Guidance** — Poojas, homas, samskaras, and their procedures\n- **Sampradaya Education** — Understanding Hindu traditions and lineages\n- **Priest Matching** — Finding the right priest for your ceremony\n\nWhat spiritual guidance can I offer you today?',
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [conversationHistory, setConversationHistory] = useState<Array<{ role: string; content: string }>>([]);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { response, isLoading, error, sendMessage } = useChat('GEMINI', 'gemini/gemini-2.5-flash', true);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  // Track streaming response and add to messages when done
  const lastResponseRef = useRef('');
  const isStreamingRef = useRef(false);

  useEffect(() => {
    if (isLoading && response) {
      isStreamingRef.current = true;
      lastResponseRef.current = response;
    }
    if (!isLoading && isStreamingRef.current && lastResponseRef.current) {
      isStreamingRef.current = false;
      const finalResponse = lastResponseRef.current;
      lastResponseRef.current = '';
      setMessages(prev => {
        // Remove any streaming placeholder, add final
        const withoutStreaming = prev.filter(m => m.role !== 'assistant' || m.content !== '__streaming__');
        return [...withoutStreaming, { role: 'assistant', content: finalResponse, timestamp: new Date() }];
      });
      setConversationHistory(prev => [...prev, { role: 'assistant', content: finalResponse }]);
    }
  }, [response, isLoading]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, response]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    setShowScrollBtn(!isNearBottom);
  };

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    const userMessage = input.trim();
    setInput('');

    const newUserMsg: Message = { role: 'user', content: userMessage, timestamp: new Date() };
    setMessages(prev => [...prev, newUserMsg]);

    const newHistory = [...conversationHistory, { role: 'user', content: userMessage }];
    setConversationHistory(newHistory);

    const apiMessages = [
      { role: 'system', content: SYSTEM_PROMPT },
      ...newHistory,
    ];

    sendMessage(apiMessages, { temperature: 0.7, max_tokens: 2048 });

    // Add streaming placeholder
    setMessages(prev => [...prev, { role: 'assistant', content: '__streaming__', timestamp: new Date() }]);
  };

  const handleQuickPrompt = (text: string) => {
    setInput(text);
    textareaRef.current?.focus();
  };

  const handleReset = () => {
    setMessages([
      {
        role: 'assistant',
        content: '🙏 **Namaste!** I am Yagnick AI, your personal Vedic guide.\n\nI can help you with:\n- **Ritual Guidance** — Poojas, homas, samskaras, and their procedures\n- **Sampradaya Education** — Understanding Hindu traditions and lineages\n- **Priest Matching** — Finding the right priest for your ceremony\n\nWhat spiritual guidance can I offer you today?',
        timestamp: new Date(),
      },
    ]);
    setConversationHistory([]);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const adjustTextarea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
  };

  return (
    <div className="min-h-screen bg-ivory relative overflow-hidden flex flex-col">
      <Toaster position="top-center" />

      {/* Rotating Mandala Backgrounds */}
      <div
        className="fixed top-[-10%] right-[-10%] w-[500px] h-[500px] opacity-[0.04] animate-rotate-slow pointer-events-none"
        style={{ backgroundImage: `url("${MANDALA_SVG}")`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat' }}
      />
      <div
        className="fixed bottom-[-15%] left-[-10%] w-[450px] h-[450px] opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("${MANDALA_SVG}")`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          animation: 'rotateSlow 30s linear infinite reverse',
        }}
      />
      <div
        className="fixed top-[40%] left-[50%] -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `url("${MANDALA_SVG}")`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          animation: 'rotateSlow 45s linear infinite',
        }}
      />

      {/* Ambient glow blobs */}
      <div className="fixed top-20 right-20 w-64 h-64 bg-saffron/5 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-32 left-10 w-48 h-48 bg-gold/5 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gold/20 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              href="/home-screen"
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-saffron/10 transition-colors text-maroon/60 hover:text-saffron"
            >
              <ArrowLeft size={20} />
            </Link>
            <div className="flex items-center gap-2.5">
              <div className="relative w-10 h-10 rounded-full saffron-gradient flex items-center justify-center shadow-md glow-saffron">
                <span className="text-xl">🪔</span>
                <div className="absolute inset-0 rounded-full animate-glow-pulse opacity-50" />
              </div>
              <div>
                <div className="font-display text-base font-bold text-maroon leading-tight">Yagnick AI</div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-[11px] text-maroon/50 font-body">Vedic Guide · Always Available</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1.5 bg-gold/10 border border-gold/30 px-3 py-1 rounded-full">
              <Sparkles size={11} className="text-gold" />
              <span className="text-[11px] font-medium text-gold">Gemini AI</span>
            </div>
            <button
              onClick={handleReset}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-maroon/10 transition-colors text-maroon/50 hover:text-maroon"
              title="New conversation"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* Quick Prompts */}
      <div className="sticky top-16 z-30 bg-ivory/90 backdrop-blur-sm border-b border-gold/10 py-3 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {QUICK_PROMPTS.map((prompt, i) => (
              <button
                key={i}
                onClick={() => handleQuickPrompt(prompt.text)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all duration-200 ${CATEGORY_COLORS[prompt.category]}`}
              >
                <span>{prompt.emoji}</span>
                <span className="whitespace-nowrap">{prompt.text}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto py-6 px-4"
        style={{ paddingBottom: '140px' }}
      >
        <div className="max-w-3xl mx-auto space-y-5">
          {messages.map((msg, idx) => (
            <MessageBubble
              key={idx}
              message={msg}
              isStreaming={msg.content === '__streaming__' && isLoading}
              streamingContent={msg.content === '__streaming__' ? response : undefined}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Scroll to bottom button */}
      {showScrollBtn && (
        <button
          onClick={scrollToBottom}
          className="fixed bottom-36 right-6 w-10 h-10 bg-maroon text-ivory rounded-full shadow-lg flex items-center justify-center hover:bg-maroon/90 transition-all z-40 animate-fade-in-up"
        >
          <ChevronDown size={18} />
        </button>
      )}

      {/* Input Area */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-xl border-t border-gold/20 px-4 py-4 pb-safe">
        <div className="max-w-3xl mx-auto">
          <div className="glass-card rounded-2xl border border-gold/30 shadow-spiritual overflow-hidden">
            <div className="flex items-end gap-3 p-3">
              <div className="flex-1">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={adjustTextarea}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about rituals, traditions, or finding a priest..."
                  disabled={isLoading}
                  rows={1}
                  className="w-full bg-transparent text-maroon placeholder-maroon/40 text-sm font-body resize-none outline-none leading-relaxed"
                  style={{ minHeight: '24px', maxHeight: '120px' }}
                />
              </div>
              <button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                  input.trim() && !isLoading
                    ? 'saffron-gradient text-white shadow-md btn-glow'
                    : 'bg-maroon/10 text-maroon/30 cursor-not-allowed'
                }`}
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <Send size={16} />
                )}
              </button>
            </div>
            <div className="px-3 pb-2 flex items-center justify-between">
              <span className="text-[10px] text-maroon/30 font-body">Press Enter to send · Shift+Enter for new line</span>
              <span className="text-[10px] text-maroon/30 font-body">{messages.length - 1} messages</span>
            </div>
          </div>
          <p className="text-center text-[10px] text-maroon/30 mt-2 font-body">
            🙏 Yagnick AI provides guidance based on Vedic traditions. Always consult a qualified priest for specific rituals.
          </p>
        </div>
      </div>
    </div>
  );
}

// Message Bubble Component
interface MessageBubbleProps {
  message: Message;
  isStreaming?: boolean;
  streamingContent?: string;
}

function MessageBubble({ message, isStreaming, streamingContent }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const displayContent = isStreaming ? (streamingContent || '') : message.content;

  if (isStreaming && !streamingContent) {
    return (
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full saffron-gradient flex items-center justify-center flex-shrink-0 shadow-md">
          <span className="text-base">🪔</span>
        </div>
        <div className="glass-card rounded-2xl rounded-tl-sm px-5 py-4 border border-gold/20 max-w-[85%]">
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              <div className="w-2 h-2 rounded-full bg-saffron animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 rounded-full bg-gold animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 rounded-full bg-sandalwood animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="text-xs text-maroon/40 font-body">Seeking wisdom...</span>
          </div>
        </div>
      </div>
    );
  }

  if (isUser) {
    return (
      <div className="flex items-start gap-3 flex-row-reverse animate-fade-in-up">
        <div className="w-9 h-9 rounded-full bg-maroon flex items-center justify-center flex-shrink-0 shadow-md">
          <span className="text-base">🙏</span>
        </div>
        <div
          className="rounded-2xl rounded-tr-sm px-5 py-3.5 max-w-[80%] shadow-sm"
          style={{ background: 'linear-gradient(135deg, #7B1C1C 0%, #500A0A 100%)' }}
        >
          <p className="text-ivory text-sm font-body leading-relaxed">{displayContent}</p>
          <p className="text-ivory/30 text-[10px] mt-1.5 text-right font-body">
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 animate-fade-in-up">
      <div className="w-9 h-9 rounded-full saffron-gradient flex items-center justify-center flex-shrink-0 shadow-md glow-saffron">
        <span className="text-base">🪔</span>
      </div>
      <div className="glass-card rounded-2xl rounded-tl-sm px-5 py-4 border border-gold/20 max-w-[85%] shadow-spiritual">
        <div className="prose prose-sm max-w-none text-maroon font-body leading-relaxed
          prose-headings:font-display prose-headings:text-maroon prose-headings:font-semibold
          prose-strong:text-maroon prose-strong:font-semibold
          prose-p:text-maroon/90 prose-p:leading-relaxed
          prose-li:text-maroon/90 prose-ul:text-maroon/90
          prose-a:text-saffron prose-a:no-underline hover:prose-a:underline
          prose-code:text-saffron prose-code:bg-saffron/10 prose-code:px-1 prose-code:rounded
          prose-blockquote:border-l-gold prose-blockquote:text-maroon/70">
          <ReactMarkdown>{displayContent}</ReactMarkdown>
        </div>
        {isStreaming && (
          <span className="inline-block w-0.5 h-4 bg-saffron ml-0.5 animate-pulse" />
        )}
        {!isStreaming && (
          <p className="text-maroon/30 text-[10px] mt-2 font-body">
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </div>
    </div>
  );
}
